import * as React from "react";

import PropTypes from "prop-types";

import Util from "@lululemon/ecom-common/src/lib/util";
import { ErrorBoundary } from "@lululemon/mwa-logger";
import { productEdHandleError } from "@lululemon/ecom-common/src/util/product-education-logger";
import { log } from "@lululemon/mwa-utils";
import { HeroPlaceholder } from "../placeholder/placeholder";

import CDPHeroBanner from "./cdp-hero-banner/cdp-hero-banner";
import CDPHeroNavigation from "./cdp-hero-navigation/cdp-hero-navigation";

import styles from "./cdp-hero.module.scss";

class CdpHero extends React.Component {
  constructor(props) {
    super(props);
    this.validateData = this.validateData.bind(this);
    this.util = new Util("components/cdp-hero");
  }

  static get componentName() {
    return "cdp-hero";
  }

  get componentData() {
    const { data } = this.props;
    const {
      title,
      subTitle,
      textColor,
      textPosition,
      image,
      components,
      callToAction,
      callToActionUrl,
    } = data;

    const isContentfulImage = image && typeof image === "object";
    const toNavigationData = (contentfulObj) => {
      const {
        callToAction: text,
        webRoute: url,
        linkAttributes,
      } = contentfulObj.fields;

      if (Array.isArray(linkAttributes?.rel)) {
        const { rel } = linkAttributes;
        linkAttributes.rel = linkAttributes?.rel.length
          ? rel.join(" ")
          : undefined;
      }

      return { text, url, linkAttributes };
    };

    const validCallToActions = (contentfulObj) => {
      const { callToAction: text, webRoute: url } = contentfulObj.fields;
      return (
        !!text && typeof text === "string" && !!url && typeof url === "string"
      );
    };

    return {
      title: title || data.heading,
      subTitle: subTitle || data.subheading,
      textColor,
      textPosition,
      imageUrl: isContentfulImage ? image.fields.imageUrl : image,
      imageDescription: isContentfulImage
        ? image.fields.imageDescription
        : data.imageAlt,
      navigation:
        components?.filter(validCallToActions).map(toNavigationData) ||
        data.navigation,
      callToAction: callToAction || data.ctatext,
      callToActionUrl: callToActionUrl || data.ctalink,
    };
  }

  get isMobile() {
    const { currentBreakpoint } = this.props;
    return {
      small: true,
      mobile: true,
      tablet: true,
    }[currentBreakpoint];
  }

  validateData() {
    const { currentPage } = this.props;
    const rules = { currentPage };
    const required = this.util.requiredData(rules);
    if (!required.pass) {
      log.warning("Missing required data", required.missing);
    }
    return required;
  }

  render() {
    if (!this.validateData().pass) return null;

    const {
      currentPage,
      componentRef,
      ctaTrack: ctaAnalyticsTrack,
      refresh,
      currentBreakpoint,
      isMarkdown,
    } = this.props;

    const {
      imageUrl,
      imageDescription,
      title,
      subTitle,
      callToAction,
      callToActionUrl,
      textColor,
      textPosition,
      navigation,
    } = this.componentData;

    if (this.isMobile && !navigation?.length) return null;

    if (refresh && !this.isMobile) return <HeroPlaceholder />;

    const showBanner = !this.isMobile;
    const showNavigation = !!navigation?.length && currentPage;

    return (
      <ErrorBoundary
        onError={(error) => productEdHandleError(error, "CdpHero", "CDP Hero")}
      >
        <div className={styles.cdpHero} id="cdpHeroSlider" ref={componentRef}>
          {showBanner && (
            <CDPHeroBanner
              {...{
                imageUrl,
                imageDescription,
                title,
                subTitle,
                callToAction,
                callToActionUrl,
                textColor,
                textPosition,
                ctaAnalyticsTrack,
              }}
            />
          )}
          {showNavigation && (
            <CDPHeroNavigation
              {...{
                currentBreakpoint,
                navigation,
                isMarkdown,
                currentPage,
              }}
            />
          )}
        </div>
      </ErrorBoundary>
    );
  }
}

CdpHero.defaultProps = {
  data: {},
  ctaTrack: "",
  refresh: false,
  isMarkdown: false,
  currentBreakpoint: null,
};

CdpHero.propTypes = {
  componentRef: PropTypes.func.isRequired,
  data: PropTypes.oneOfType([
    PropTypes.shape({
      heading: PropTypes.string,
      subheading: PropTypes.string,
      textColor: PropTypes.string,
      textPosition: PropTypes.string,
      image: PropTypes.string,
      imageAlt: PropTypes.string,
      navigation: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
        })
      ),
      ctalink: PropTypes.string,
      ctatext: PropTypes.string,
    }),
    PropTypes.shape({
      title: PropTypes.string,
      subTitle: PropTypes.string,
      textColor: PropTypes.string,
      textPosition: PropTypes.string,
      callToAction: PropTypes.string,
      callToActionUrl: PropTypes.string,
      image: PropTypes.shape({
        fields: PropTypes.shape({
          imageUrl: PropTypes.string.isRequired,
          imageDescription: PropTypes.string.isRequired,
        }),
      }),
      components: PropTypes.objectOf(
        PropTypes.shape({
          fields: PropTypes.arrayOf(
            PropTypes.shape({
              callToAction: PropTypes.string.isRequired,
              webRoute: PropTypes.string.isRequired,
            })
          ),
        })
      ),
    }),
  ]),
  currentBreakpoint: PropTypes.string,
  currentPage: PropTypes.string.isRequired,
  ctaTrack: PropTypes.string,
  refresh: PropTypes.bool,
  isMarkdown: PropTypes.bool,
};

export default CdpHero;
